/**
 * saveCanvasAPI.js
 * 
 * Saves customization to the TreatInk API for third-party integrations.
 * Used when customizer is in API/iframe mode.
 * 
 * Location: src/components/customizer/functions/saveCanvasAPI.js
 */

import { getImageFromDB } from '../../../utils/indexedDB';

export const saveCanvasAPI = async ({
  sessionUuid,
  supabaseUrl,
  petType,
  petName,
  selectedFrame,
  canvasRef,
  image,
  uploadedImageIds,
  toast,
  setNotSelectedKey,
}) => {
  // Validate pet type is selected
  if (!petType) {
    setNotSelectedKey((prev) => prev + 1);
    toast.error('Please select a pet type (Dog or Cat)');
    throw new Error('Pet type not selected');
  }

  // Get canvas blob
  const canvas = canvasRef.current;
  if (!canvas) {
    throw new Error('Canvas not available');
  }

  const canvasBlob = await new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/png', 1.0);
  });

  if (!canvasBlob) {
    throw new Error('Failed to create image from canvas');
  }

  // Get original uploaded image from IndexedDB
  let originalBlob = null;
  if (uploadedImageIds && uploadedImageIds.length > 0) {
    try {
      originalBlob = await getImageFromDB('uploadedImages', uploadedImageIds[0]);
      console.log('[saveCanvasAPI] Retrieved original image from IndexedDB');
    } catch (err) {
      console.warn('[saveCanvasAPI] Could not retrieve original image:', err);
    }
  }

  // Upload artwork to API
  const formData = new FormData();
  formData.append('sessionUuid', sessionUuid);
  formData.append('canvasImage', canvasBlob, 'customization.png');
  formData.append('petType', petType);
  formData.append('petName', petName || '');
  formData.append('labelTemplate', selectedFrame?.name || selectedFrame?.url || '');
  
  // Add original image if available
  if (originalBlob) {
    formData.append('originalImage', originalBlob, 'original-photo.png');
    console.log('[saveCanvasAPI] Including original image in upload');
  }

  console.log('[saveCanvasAPI] Uploading artwork for session:', sessionUuid);

  const response = await fetch(
    `${supabaseUrl}/functions/v1/upload-customization-artwork`,
    {
      method: 'POST',
      body: formData,
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error('[saveCanvasAPI] Upload failed:', errorData);
    toast.error(errorData.error || 'Failed to upload artwork');
    throw new Error(errorData.error || 'Failed to upload artwork');
  }

  const result = await response.json();
  
  if (!result.success) {
    toast.error(result.error || 'Failed to save customization');
    throw new Error(result.error || 'Failed to save customization');
  }

  console.log('[saveCanvasAPI] Artwork saved successfully:', result);
  toast.success('Customization saved!');
  return result;
};
